/**
 * Client-side password policy: SINGLE SOURCE OF TRUTH.
 *
 * 2026-07-19 miss-hunt P1 (env-supabase-B): GoTrue on the shared auth project
 * enforces password_min_length=12, but every screen validated and advertised
 * 8, so a user typing a 9-char password passed the client check and then got a
 * misleading server error. Every length check, `minLength` attribute and hint
 * string MUST come from here.
 *
 * 2026-08-27 register row SGN-02 (P1). Paul, on the firm signup screen during
 * the 25 August walkthrough: the password was accepted with no complexity rule
 * at all. His words: "we need at least one capital letter and one special
 * character and a number." The policy is now four rules, not one.
 *
 * WHY THE RULES LIVE IN A LIST AND NOT IN AN `if`. Three screens take a new
 * password (signup, reset-password, the change-password panel on /app/security)
 * and every earlier copy fix in this project was applied to the one screen the
 * reviewer was looking at, leaving the other render path weaker. A list that
 * both the checklist UI and the submit check read from is the only shape where
 * a screen cannot quietly enforce less than its neighbour. There is no exported
 * length-only helper any more, on purpose: if one exists, a future screen will
 * import it and ship the old weaker gate.
 *
 * WHAT THIS IS NOT. A client check is a courtesy, not enforcement. The real
 * gate for signup is GoTrue's own project password policy on Supabase, which is
 * configuration this repo does not hold. See the note in the SGN-02 report.
 */
export const PASSWORD_MIN_LENGTH = 12;

/** Stable ids so a test can name one rule without matching on copy. */
export type PasswordRuleId = 'length' | 'capital' | 'number' | 'symbol';

export interface PasswordRule {
  id: PasswordRuleId;
  /** Checklist line under the field. Reads as a thing you can tick off. */
  label: string;
  /** Completes the sentence "Your password needs ...". */
  need: string;
  /** True when the typed value satisfies this rule. */
  isMet: (value: string) => boolean;
}

/**
 * A symbol is anything that is not a letter, not a digit and not a space. A
 * fixed list would reject the Rand sign and every accented key an Afrikaans
 * keyboard produces, and a person who typed one would be told, wrongly, that
 * they had not typed a symbol.
 */
const CAPITAL = /\p{Lu}/u;
const NUMBER = /\p{Nd}/u;
const SYMBOL = /[^\p{L}\p{N}\s]/u;

export const PASSWORD_RULES: readonly PasswordRule[] = [
  {
    id: 'length',
    label: `At least ${PASSWORD_MIN_LENGTH} characters`,
    need: `at least ${PASSWORD_MIN_LENGTH} characters`,
    isMet: (value) => value.length >= PASSWORD_MIN_LENGTH,
  },
  {
    id: 'capital',
    label: 'One capital letter',
    need: 'a capital letter',
    isMet: (value) => CAPITAL.test(value),
  },
  {
    id: 'number',
    label: 'One number',
    need: 'a number',
    isMet: (value) => NUMBER.test(value),
  },
  {
    id: 'symbol',
    label: 'One symbol, such as ! or #',
    need: 'a symbol such as ! or #',
    isMet: (value) => SYMBOL.test(value),
  },
];

/** THE validator. Returns the rules the value has not met yet, in order. */
export function unmetPasswordRules(value: string): PasswordRule[] {
  return PASSWORD_RULES.filter((rule) => !rule.isMet(value));
}

/** True only when every rule passes. */
export function passwordMeetsPolicy(value: string): boolean {
  return unmetPasswordRules(value).length === 0;
}

/**
 * Join the missing rules into one sentence a person can act on: "Your password
 * needs a capital letter and a number." Never a bare "invalid password", which
 * tells someone to guess.
 */
export function passwordProblem(value: string, subject = 'Your password'): string | null {
  const unmet = unmetPasswordRules(value);
  if (unmet.length === 0) return null;
  const needs = unmet.map((rule) => rule.need);
  const last = needs[needs.length - 1] as string;
  const sentence =
    needs.length === 1 ? last : `${needs.slice(0, -1).join(', ')} and ${last}`;
  return `${subject} needs ${sentence}.`;
}
